'use client';

/**
 * Discovery Graph Reagraph
 *
 * @component_role Discovery Results Graph Visualization
 * @business_rule Displays discovery search results as an interactive WebGL graph
 *   using Reagraph. Shows relationships between products, vendors, and collections.
 * @user_outcome Explore discovery results visually - see product relationships,
 *   vendor networks, and collection groupings at a glance.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain discovery
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Loader2,
  Network,
  ZoomIn,
  ZoomOut,
  Maximize2,
  MousePointer2,
  Copy,
  Eye,
  Box,
  Factory,
  Layers,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import type { DiscoveryGraphNode } from '@/lib/api/types';

interface DiscoveryGraphReagraphProps {
  nodes: DiscoveryGraphNode[];
  edges: { source: string; target: string; type: string }[];
  onNodeClick?: (node: DiscoveryGraphNode) => void;
}

// Node colors by type - matches discovery entity types
const NODE_COLORS: Record<string, string> = {
  Product: '#4DD0E1',      // Cyan for products
  Actor: '#BD10E0',        // Purple for vendors
  Collection: '#50E3C2',   // Teal for collections
  Category: '#F5A623',     // Orange for categories
};

// Node sizes by type - same scale as project graph
const NODE_SIZES: Record<string, number> = {
  Actor: 12,        // Vendors (same as project)
  Collection: 10,   // Collections
  Category: 10,     // Categories
  Product: 6,       // Products
};

// Edge colors by relationship type
const EDGE_COLORS: Record<string, string> = {
  BELONGS_TO: '#BD10E0',
  HAS_PRODUCT: '#50E3C2',
  IN_COLLECTION: '#4DD0E1',
  SUPPLIES: '#F5A623',
  default: '#666666',
};

export function DiscoveryGraphReagraph({
  nodes,
  edges,
  onNodeClick,
}: DiscoveryGraphReagraphProps) {
  const [isClient, setIsClient] = useState(false);
  const [GraphCanvas, setGraphCanvas] = useState<any>(null);
  const [themes, setThemes] = useState<{ dark: any; light: any } | null>(null);
  const { resolvedTheme } = useTheme();
  const selectNode = useGraphSelectionStore((state) => state.selectNode);
  const graphRef = useRef<any>(null);

  // Selection and interaction state
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
  const [activeNodes, setActiveNodes] = useState<string[]>([]);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    node: any;
  } | null>(null);

  // Clustering toggle - off by default for cleaner look
  const [clusteringEnabled, setClusteringEnabled] = useState(false);

  // Node limits per type (expand to show all)
  const [showAllProducts, setShowAllProducts] = useState(false);
  const MAX_PRODUCTS = 20;

  // Load ReaGraph only on client
  useEffect(() => {
    setIsClient(true);
    import('reagraph').then((mod) => {
      setGraphCanvas(() => mod.GraphCanvas);

      // Customize themes
      const customDarkTheme = {
        ...mod.darkTheme,
        canvas: {
          ...mod.darkTheme.canvas,
          background: '#121212',
        },
        node: {
          ...mod.darkTheme.node,
          activeFill: '#ffffff',
          inactiveOpacity: 0.3,
        },
        edge: {
          ...mod.darkTheme.edge,
          activeFill: '#ffffff',
          inactiveOpacity: 0.2,
        },
        cluster: {
          ...mod.darkTheme.cluster,
          stroke: '#ffffff22',
          fill: '#ffffff11',
          label: {
            stroke: '#ffffff',
            color: '#ffffff',
          },
        },
      };
      const customLightTheme = {
        ...mod.lightTheme,
        canvas: {
          ...mod.lightTheme.canvas,
          background: '#ffffff',
        },
        node: {
          ...mod.lightTheme.node,
          activeFill: '#000000',
          inactiveOpacity: 0.3,
        },
        edge: {
          ...mod.lightTheme.edge,
          activeFill: '#000000',
          inactiveOpacity: 0.2,
        },
        cluster: {
          ...mod.lightTheme.cluster,
          stroke: '#00000022',
          fill: '#00000011',
          label: {
            stroke: '#000000',
            color: '#000000',
          },
        },
      };
      setThemes({ dark: customDarkTheme, light: customLightTheme });
    });
  }, []);

  // Transform Discovery data to ReaGraph format with limits
  const { graphNodes, graphEdges, hiddenProductCount } = useMemo(() => {
    // Separate nodes by type
    const products = nodes.filter((n) => n.type === 'Product');
    const others = nodes.filter((n) => n.type !== 'Product');

    // Apply limit to products
    const visibleProducts = showAllProducts ? products : products.slice(0, MAX_PRODUCTS);
    const hiddenCount = products.length - visibleProducts.length;

    const visibleNodes = [...others, ...visibleProducts];
    const visibleNodeIds = new Set(visibleNodes.map((n) => n.id));

    const graphNodes = visibleNodes.map((node) => ({
      id: node.id,
      label: node.label,
      fill: NODE_COLORS[node.type] || '#8AB4F8',
      size: NODE_SIZES[node.type] || 8,
      cluster: node.type,
      data: {
        type: node.type,
        ...node.properties,
      },
    }));

    // Only include edges where both nodes are visible
    const graphEdges = edges
      .filter((edge) => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target))
      .map((edge, idx) => ({
        id: `edge-${idx}-${edge.source}-${edge.target}`,
        source: edge.source,
        target: edge.target,
        label: edge.type?.replace(/_/g, ' ') || '',
        fill: EDGE_COLORS[edge.type || ''] || EDGE_COLORS.default,
        size: 1,
      }));

    return { graphNodes, graphEdges, hiddenProductCount: hiddenCount };
  }, [nodes, edges, showAllProducts]);

  // Handle node click
  const handleNodeClick = useCallback(
    (node: any) => {
      setSelectedNodes([node.id]);

      // Map reagraph node back to DiscoveryGraphNode
      const originalNode = nodes.find((n) => n.id === node.id);
      if (originalNode) {
        // Call external handler if provided
        onNodeClick?.(originalNode);

        // Also update global selection store
        selectNode({
          id: originalNode.id,
          label: originalNode.label,
          type: originalNode.type === 'Actor' ? 'vendor' : originalNode.type.toLowerCase(),
          properties: originalNode.properties,
        });
      }
    },
    [nodes, onNodeClick, selectNode]
  );

  // Handle node hover
  const handleNodePointerOver = useCallback((node: any) => {
    setActiveNodes([node.id]);
  }, []);

  const handleNodePointerOut = useCallback(() => {
    setActiveNodes([]);
  }, []);

  // Handle right-click context menu
  const handleNodeContextMenu = useCallback((node: any, event: any) => {
    event.preventDefault?.();
    setContextMenu({
      x: event.clientX || event.x || 100,
      y: event.clientY || event.y || 100,
      node,
    });
  }, []);

  // Close context menu
  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Context menu actions
  const handleViewDetails = useCallback(() => {
    if (contextMenu?.node) {
      const originalNode = nodes.find((n) => n.id === contextMenu.node.id);
      if (originalNode) {
        selectNode({
          id: originalNode.id,
          label: originalNode.label,
          type: originalNode.type === 'Actor' ? 'vendor' : originalNode.type.toLowerCase(),
          properties: originalNode.properties,
        });
      }
    }
    closeContextMenu();
  }, [contextMenu, nodes, selectNode, closeContextMenu]);

  const handleCopyId = useCallback(() => {
    if (contextMenu?.node) {
      navigator.clipboard.writeText(contextMenu.node.id);
    }
    closeContextMenu();
  }, [contextMenu, closeContextMenu]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    graphRef.current?.zoomIn?.();
  }, []);

  const handleZoomOut = useCallback(() => {
    graphRef.current?.zoomOut?.();
  }, []);

  const handleFitView = useCallback(() => {
    graphRef.current?.fitNodesInView?.();
  }, []);

  // Close context menu on click outside
  useEffect(() => {
    if (contextMenu) {
      const handleClick = () => closeContextMenu();
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu, closeContextMenu]);

  if (!isClient) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-12 h-12 animate-spin mb-4" />
        <p>Loading graph...</p>
      </div>
    );
  }

  if (!graphNodes.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Network className="w-12 h-12 mb-4 opacity-50" />
        <p>No graph data available</p>
        <p className="text-sm mt-2">Search to populate the graph</p>
      </div>
    );
  }

  if (!GraphCanvas) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-12 h-12 animate-spin mb-4" />
        <p>Loading graph renderer...</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-panel-border bg-panel">
        <div className="flex items-center gap-2">
          {/* Clustering toggle */}
          <button
            onClick={() => setClusteringEnabled(!clusteringEnabled)}
            className={`px-2 py-1 rounded text-xs transition-colors ${
              clusteringEnabled
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted'
            }`}
            title="Toggle clustering by entity type"
          >
            Cluster
          </button>

          {/* Show all products toggle */}
          {hiddenProductCount > 0 && (
            <button
              onClick={() => setShowAllProducts(true)}
              className="px-2 py-1 rounded text-xs bg-muted text-muted-foreground hover:bg-muted transition-colors"
            >
              +{hiddenProductCount} more products
            </button>
          )}
          {showAllProducts && nodes.filter((n) => n.type === 'Product').length > MAX_PRODUCTS && (
            <button
              onClick={() => setShowAllProducts(false)}
              className="px-2 py-1 rounded text-xs bg-muted text-muted-foreground hover:bg-muted transition-colors"
            >
              Show less
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Zoom controls */}
          <div className="flex items-center border-l border-panel-border pl-2">
            <button
              onClick={handleZoomIn}
              className="p-1.5 rounded hover:bg-muted transition-colors"
              title="Zoom in"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button
              onClick={handleZoomOut}
              className="p-1.5 rounded hover:bg-muted transition-colors"
              title="Zoom out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <button
              onClick={handleFitView}
              className="p-1.5 rounded hover:bg-muted transition-colors"
              title="Fit to view"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-3 text-xs text-muted-foreground border-l border-panel-border pl-2">
            <span>{graphNodes.length} nodes</span>
            <span>{graphEdges.length} edges</span>
          </div>
        </div>
      </div>

      {/* Graph Container */}
      <div
        className="flex-1 bg-background relative overflow-hidden"
        style={{ minHeight: '400px' }}
        suppressHydrationWarning
      >
        <div className="absolute inset-0">
          <GraphCanvas
            ref={graphRef}
            nodes={graphNodes}
            edges={graphEdges}
            layoutType="forceDirected2d"
            clusterAttribute={clusteringEnabled ? 'cluster' : undefined}
            labelType="nodes"
            draggable
            animated
            cameraMode="pan"
            edgeArrowPosition="end"
            edgeLabelPosition="above"
            theme={themes ? (resolvedTheme === 'dark' ? themes.dark : themes.light) : undefined}
            selections={selectedNodes}
            actives={activeNodes}
            onNodeClick={handleNodeClick}
            onNodePointerOver={handleNodePointerOver}
            onNodePointerOut={handleNodePointerOut}
            onNodeContextMenu={handleNodeContextMenu}
          />
        </div>

        {/* Context Menu */}
        {contextMenu && (
          <div
            className="absolute z-50 bg-popover border border-border rounded-md shadow-lg py-1 min-w-[160px]"
            style={{ left: contextMenu.x, top: contextMenu.y }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-1.5 text-xs text-muted-foreground border-b border-border mb-1">
              {contextMenu.node.data?.type}: {contextMenu.node.label}
            </div>
            <button
              onClick={handleViewDetails}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted transition-colors"
            >
              <Eye className="w-4 h-4" />
              View Details
            </button>
            <button
              onClick={handleCopyId}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted transition-colors"
            >
              <Copy className="w-4 h-4" />
              Copy ID
            </button>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-between px-3 py-2 border-t border-panel-border bg-panel text-xs">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            <Box className="w-3 h-3" style={{ color: NODE_COLORS.Product }} />
            <span className="text-muted-foreground">Product</span>
          </div>
          <div className="flex items-center gap-1">
            <Factory className="w-3 h-3" style={{ color: NODE_COLORS.Actor }} />
            <span className="text-muted-foreground">Vendor</span>
          </div>
          <div className="flex items-center gap-1">
            <Layers className="w-3 h-3" style={{ color: NODE_COLORS.Collection }} />
            <span className="text-muted-foreground">Collection</span>
          </div>
        </div>
        <div className="text-muted-foreground">
          <MousePointer2 className="w-3 h-3 inline mr-1" />
          Click to select • Right-click for menu
        </div>
      </div>
    </div>
  );
}
